import React, { useEffect, useState } from "react";
import { useLayout } from "../../hooks/useLayout";

import { Checkbox, Select } from "@in/component-library";
import { Button, Grid, GridItem, Input, Text, VerticalSpacer } from "@innovationnorway/lighthouse-components";
import useSWR from "swr";
import { ErrorCodes } from "../../../main/models/error-codes";
import { UpsertUserAccountModel } from "../../../main/models/upsert-user-account-model";
import { UpsertUserAccountResponse } from "../../../main/models/upsert-user-account-response";
import { UserAccount } from "../../../main/models/user-account";
import { DEFAULT_COUNTRY_CODE } from "../../../utils/constants";
import countries from "../../../utils/countries";
import { GetValidatedObject } from "../../../utils/validationUtils";

import { getUserAccounts } from "../../apis/getUserAccounts";
import { upsertUserAccount } from "../../apis/upsertUserAccount";
import { useErrors } from "../../hooks/useErrors";
import { InfoBanner } from "../infoBanner/infoBanner";
import "./verifyEmailForm.scss";

interface VerifyEmailFormProps {
  accountPublicId: string;
  suggestedEmail?: string;
  suggestedPhoneNumber?: string;
  suggestedNewletterConsent?: boolean;
  onSuccess: (verified: boolean, emailVerificationSent: boolean, emailAddress: string, phoneNumber: string) => void;
}

const VerifyEmailForm = ({
  accountPublicId,
  suggestedEmail,
  suggestedPhoneNumber,
  suggestedNewletterConsent,
  onSuccess
}: VerifyEmailFormProps) => {
  const { translations } = useLayout();
  const errorCodes = useErrors();
  const { data: userAccounts, isValidating, mutate } = useSWR(`userAccounts`, getUserAccounts);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(suggestedEmail || "");
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [phoneNumber, setPhoneNumber] = useState<string>(suggestedPhoneNumber || "");
  const [validationObject, setValidationObject] = useState<UpsertUserAccountModel>();
  const [newsletterConsent, setNewsletterConsent] = useState<boolean>(suggestedNewletterConsent || false);
  const [emailIsUsedInOtherAccount, setEmailIsUsedInOtherAccount] = useState<boolean>(false);
  const [emailIsVerified, setEmailIsVerified] = useState<boolean>(false);

  useEffect(() => {
    if (suggestedEmail) {
      setEmail(suggestedEmail);
    }
    if (suggestedPhoneNumber) {
      const countryCodeinPhoneNumber = countries.find((c) => suggestedPhoneNumber.includes(c.dialCode));
      if (countryCodeinPhoneNumber) {
        setCountryCode(countryCodeinPhoneNumber.dialCode);
        setPhoneNumber(suggestedPhoneNumber.replace(countryCodeinPhoneNumber.dialCode, ""));
      } else {
        setPhoneNumber(suggestedPhoneNumber);
      }
    }
    if (suggestedNewletterConsent) {
      setNewsletterConsent(suggestedNewletterConsent);
    }
  }, [suggestedEmail, suggestedPhoneNumber, suggestedNewletterConsent]);

  useEffect(() => {
    const userAccountsWithEmail =
      userAccounts?.filter((ua: UserAccount) => ua.emailAddress == email && ua.emailVerified) ?? [];
    setEmailIsVerified(userAccountsWithEmail.length > 0);
    setEmailIsUsedInOtherAccount(
      userAccountsWithEmail.filter((ua) => ua.account.publicId !== accountPublicId).length > 0
    );
  }, [isValidating, email]);

  const submit = async () => {
    setIsSaving(true);
    setValidationObject(undefined);
    const response = await upsertUserAccount("userAccounts", {
      email: email,
      phoneNumber: phoneNumber,
      countryCode: countryCode,
      newsletterConsent: newsletterConsent,
      accountPublicId: accountPublicId
    });

    if (response.code === 200) {
      const addResponse = response.body as UpsertUserAccountResponse;
      if (!addResponse.emailVerificationSent) {
        mutate();
      }
      onSuccess(addResponse.verified, addResponse.emailVerificationSent, email, phoneNumber);
    }
    const validationObject = GetValidatedObject<UpsertUserAccountModel>(response);
    setValidationObject(validationObject);

    setIsSaving(false);
  };

  return (
    <Grid className="verify-email-form">
      <GridItem spanLarge={10} spanMedium={8} spanSmall={3}>
        <Text as="label">
          {translations.verifyEmailEmailLabel}
          <Input
            className="data-hj-suppress"
            name="email"
            type="email"
            required
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            errorMessage={errorCodes[validationObject?.email as keyof ErrorCodes]}
          />
        </Text>
      </GridItem>
      <GridItem spanLarge={10} spanMedium={8} spanSmall={4}>
        <Grid>
          <GridItem spanLarge={3} spanMedium={3} spanSmall={2}>
            <Select //TODO: Replace with select from new component library when available
              className="data-hj-suppress beta-select"
              name="countryCode"
              label={translations.countryCodeLabel}
              placeholder={translations.countryCodeLabel}
              value={countryCode}
              errorMsg={validationObject?.countryCode}
              onChange={(e) => setCountryCode(e)}
              options={[
                {
                  dialCode: DEFAULT_COUNTRY_CODE,
                  isoCode: "NO"
                },
                ...countries.sort((a, b) => a.isoCode.localeCompare(b.isoCode))
              ].map((e) => ({
                value: e.dialCode,
                text: `${e.isoCode} ${e.dialCode}`
              }))}
            />
          </GridItem>
          <GridItem spanLarge={9} spanMedium={9} spanSmall={2}>
            <Text as="label">
              {translations.verifyEmailPhoneNumberLabel}
              <Input
                className="data-hj-suppress"
                name="phone"
                type="tel"
                required
                onChange={(e) => setPhoneNumber(e.target.value)}
                value={phoneNumber}
                errorMessage={errorCodes[validationObject?.phoneNumber as keyof ErrorCodes]}
              />
            </Text>
          </GridItem>
        </Grid>
      </GridItem>
      {emailIsUsedInOtherAccount && (
        <GridItem spanLarge={10} spanMedium={10} spanSmall={4}>
          <InfoBanner variant="info">
            <Text as="span">{translations.verifyEmailEditPhonenumberForAllActors}</Text>
          </InfoBanner>
        </GridItem>
      )}
      <GridItem spanLarge={10} spanMedium={10} spanSmall={2}>
        <Text as="label">{translations.editContactNewsletterConsentTitle}</Text>
        <VerticalSpacer size="s" />
        <Checkbox //TODO: Replace with checkbox from new component library when available
          name="newsletterCheckbox"
          className="beta-checkbox"
          options={[
            {
              selected: newsletterConsent,
              value: "newsletterConsent",
              text: translations.editContactNewsletterConsentLabel
            }
          ]}
          onChange={() => setNewsletterConsent(!newsletterConsent)}
        />
      </GridItem>
      {!emailIsVerified && (
        <GridItem spanLarge={10} spanMedium={10} spanSmall={4}>
          <Text>
            {translations.verifyEmailCodeAlreadyRecieved}{" "}
            <Text className="inline_clickable_text" onClick={() => onSuccess(false, true, "", phoneNumber)}>
              {translations.clickHere}
            </Text>
          </Text>
        </GridItem>
      )}
      <GridItem spanLarge={8} spanMedium={8} spanSmall={4}>
        <Grid>
          <GridItem spanLarge={3} spanMedium={3} spanSmall={1}>
            <Button
              variant="primary"
              isLoading={isSaving}
              disabled={isSaving || !email || !phoneNumber || !countryCode}
              onClick={submit}
            >
              {emailIsUsedInOtherAccount ? translations.verifiedNextBtnLabel : translations.chooseAccountNextLabel}
            </Button>
          </GridItem>
        </Grid>
      </GridItem>
    </Grid>
  );
};

export default VerifyEmailForm;
